import React, { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { toast } from 'sonner'
import { Wrench, Check, AlertTriangle, CheckCircle2, Settings2, History } from 'lucide-react'
import DashboardLayout from '../../layouts/DashboardLayout'
import { cn } from '../../lib/utils'

export default function Index({ period, period_label, next_service, due, rows, default_signed_by, is_owner, history }) {
  const [signedBy, setSignedBy] = useState(default_signed_by || '')
  const [busy, setBusy] = useState(null) // task id currently saving

  const remaining = rows.filter((r) => !r.done).length

  function toggle(row, done) {
    if (done && !signedBy.trim()) {
      toast.error('Enter who is signing off first')
      return
    }
    setBusy(row.id)
    router.post('/service-schedule/toggle',
      { task_id: row.id, done, signed_by: signedBy },
      {
        preserveScroll: true,
        onSuccess: () => { if (done) toast.success(`Signed off: ${row.name}`) },
        onError: () => toast.error('Could not save — try again'),
        onFinish: () => setBusy(null),
      })
  }

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-3xl">
        <div className="mb-5 flex items-center gap-3">
          <Wrench className="text-brand-primary" />
          <div className="flex-1">
            <h1 className="text-xl font-semibold text-brand-ink">Service schedule</h1>
            <p className="text-sm text-brand-muted">Monthly equipment check — {period_label}</p>
          </div>
          {is_owner && (
            <Link href="/service-schedule/tasks"
              className="flex items-center gap-1.5 rounded-xl border border-brand-border bg-white px-3 py-2 text-sm font-medium text-brand-muted hover:text-brand-ink">
              <Settings2 size={15} /> Edit checklist
            </Link>
          )}
        </div>

        {/* Status banner */}
        {due ? (
          <div className="mb-5 flex items-start gap-3 rounded-2xl border border-brand-danger/30 bg-brand-danger/5 px-4 py-3">
            <AlertTriangle size={18} className="mt-0.5 flex-shrink-0 text-brand-danger" />
            <div>
              <p className="font-semibold text-brand-danger">Due this month — please complete ASAP</p>
              <p className="text-sm text-brand-danger/80">{remaining} of {rows.length} checks still to be signed off for {period_label}.</p>
            </div>
          </div>
        ) : (
          <div className="mb-5 flex items-start gap-3 rounded-2xl border border-emerald-300 bg-emerald-50 px-4 py-3">
            <CheckCircle2 size={18} className="mt-0.5 flex-shrink-0 text-emerald-600" />
            <div>
              <p className="font-semibold text-emerald-800">All done for {period_label}</p>
              <p className="text-sm text-emerald-700">Every check is signed off. The tab is clear until the 1st of next month.</p>
            </div>
          </div>
        )}

        {/* Signed by */}
        <label className="mb-4 block">
          <span className="mb-1 block text-xs font-medium text-brand-muted">Signing off as</span>
          <input value={signedBy} onChange={(e) => setSignedBy(e.target.value)} placeholder="Your name or initials"
            className="w-full rounded-xl border border-brand-border bg-white px-3 py-2.5 text-sm focus:border-brand-primary focus:outline-none sm:max-w-xs" />
        </label>

        {/* Checklist */}
        <div className="overflow-hidden rounded-2xl border border-brand-border bg-white">
          {/* Header row (desktop) */}
          <div className="hidden border-b border-brand-border bg-brand-surface px-4 py-2 text-xs font-semibold uppercase tracking-wider text-brand-muted sm:grid sm:grid-cols-[1fr_120px_120px_44px] sm:gap-3">
            <span>Equipment</span>
            <span>Last service</span>
            <span>Signed</span>
            <span className="text-right">Done</span>
          </div>
          <div className="divide-y divide-brand-border">
            {rows.map((row) => (
              <div key={row.id}
                className={cn('grid grid-cols-[1fr_44px] items-center gap-3 px-4 py-3 sm:grid-cols-[1fr_120px_120px_44px]',
                  row.done && 'bg-emerald-50/40')}>
                <div className="min-w-0">
                  <p className={cn('font-medium text-brand-ink', row.done && 'text-emerald-900')}>{row.name}</p>
                  {/* Mobile: show last/signed inline */}
                  <p className="text-xs text-brand-muted sm:hidden">
                    {row.done
                      ? `Done ${row.done_on} · ${row.signed_by}`
                      : row.last_done_on ? `Last: ${row.last_done_on} · ${row.last_signed_by}` : 'Not done yet'}
                  </p>
                </div>
                <div className="hidden text-sm text-brand-muted sm:block">
                  {row.done ? row.done_on : (row.last_done_on || '—')}
                </div>
                <div className="hidden text-sm text-brand-muted sm:block">
                  {row.done ? row.signed_by : (row.last_signed_by || '—')}
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={() => toggle(row, !row.done)}
                    disabled={busy === row.id}
                    title={row.done ? 'Undo sign-off' : 'Sign off'}
                    className={cn('flex h-7 w-7 items-center justify-center rounded-lg border transition-colors disabled:opacity-50',
                      row.done
                        ? 'border-emerald-500 bg-emerald-500 text-white hover:bg-emerald-600'
                        : 'border-brand-border bg-white text-transparent hover:border-brand-primary')}>
                    <Check size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-brand-border bg-brand-surface px-4 py-2 text-xs text-brand-muted">
            Next service due: <span className="font-medium text-brand-ink">{next_service}</span> (and the 1st of each month after).
          </div>
        </div>

        {/* History */}
        {history.length > 0 && (
          <div className="mt-6">
            <h2 className="mb-2 flex items-center gap-2 px-1 text-xs font-semibold uppercase tracking-wider text-brand-muted">
              <History size={14} /> Previous months
            </h2>
            <div className="divide-y divide-brand-border overflow-hidden rounded-2xl border border-brand-border bg-white">
              {history.map((h) => (
                <div key={h.period} className="flex items-center justify-between px-4 py-2.5 text-sm">
                  <span className="font-medium text-brand-ink">{h.label}</span>
                  <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800">{h.count} signed off</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}
